import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AlertTriangle,
  Hourglass,
  RefreshCw,
  UserCheck,
  UserX,
  Users,
  Zap,
} from 'lucide-react';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import AdminKpiCard from './AdminKpiCard';
import { AdminOnlinePopover } from './AdminOnlineIndicator';
import UserDetailsDrawer from './UserDetailsDrawer';
import {
  auditEventMeta,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelativeTime,
  formatShortDateTime,
} from './adminFormatters';
import './adminLayout.sass';

const { Text } = Typography;

const TIMELINE_COLORS = {
  blue: 'blue',
  geekblue: 'blue',
  green: 'green',
  red: 'red',
  gold: 'gold',
  orange: 'orange',
  volcano: 'red',
  default: 'gray',
};

function mergeActivity(activity) {
  if (!activity) return [];
  const items = [];

  for (const row of activity.audit || []) {
    const meta = auditEventMeta(row.event_type);
    items.push({
      key: `audit-${row.id}`,
      ts: row.created_at,
      color: TIMELINE_COLORS[meta.color] || 'gray',
      title: meta.label,
      who: row.actor_username || row.user_username || 'система',
      meta: row.ip || null,
      userId: null,
    });
  }

  for (const row of activity.tasks || []) {
    items.push({
      key: `task-${row.id}`,
      ts: row.created_at,
      color: 'green',
      title: 'Создан поиск',
      who: row.username,
      meta: `${formatNumber(row.requests_count)} подзапросов`,
      userId: row.user_id,
    });
  }

  for (const row of activity.sessions || []) {
    const finished = Boolean(row.disconnected_at);
    const durationSec = finished
      ? Math.round((new Date(row.disconnected_at) - new Date(row.connected_at)) / 1000)
      : null;
    items.push({
      key: `session-${row.id}`,
      ts: row.connected_at,
      color: finished ? 'gray' : 'blue',
      title: finished ? 'Сессия завершена' : 'Подключение к сервису',
      who: row.username,
      meta: finished ? `длительность ${formatDuration(durationSec)}` : 'сейчас в сервисе',
      userId: row.user_id,
    });
  }

  return items
    .filter((item) => item.ts)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts))
    .slice(0, 12);
}

const AdminOverview = ({ summary, onNavigate }) => {
  const { getUser, getTopUsers, getRecentActivity, onlineUserIds } = useAdminSocketContext();
  const [topUsers, setTopUsers] = useState([]);
  const [activity, setActivity] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  const overview = summary.overview;
  const downtime = summary.downtime;

  const loadDetails = useCallback(async () => {
    setDetailsLoading(true);
    try {
      const [top, recent] = await Promise.all([
        getTopUsers({ days: 30, metric: 'requests', limit: 8 }),
        getRecentActivity({ limit: 12 }),
      ]);
      setTopUsers(top.rows || []);
      setActivity(recent.activity || null);
    } catch (err) {
      message.error(err.message || 'Не удалось загрузить активность');
    } finally {
      setDetailsLoading(false);
    }
  }, [getTopUsers, getRecentActivity]);

  useEffect(() => {
    if (summary.updatedAt) {
      loadDetails();
    }
  }, [summary.updatedAt, loadDetails]);

  const hasTodayRequests = overview?.requests_period > 0;
  const errorRate = hasTodayRequests
    ? (Number(overview.error_period) / Number(overview.requests_period)) * 100
    : null;

  const onlineUsers = overview?.online_users || [];
  const onlineIds = onlineUsers.map((user) => user.id);
  const onlineCount = overview?.online_now ?? onlineIds.length ?? 0;
  const onlineSet = useMemo(
    () => new Set((onlineUserIds || []).map(Number)),
    [onlineUserIds]
  );

  const attention = useMemo(() => {
    const items = [];
    if (summary.pendingAged > 0) {
      items.push({
        key: 'pending',
        type: 'warning',
        message: `${formatNumber(summary.pendingAged)} запросов в очереди дольше 30 минут`,
        description: 'Проверьте внешнюю очередь и этапы обработки запросов.',
        tab: 'tech',
      });
    }
    if (overview?.error_period > 0) {
      items.push({
        key: 'errors',
        type: 'error',
        message: `${formatNumber(overview.error_period)} ошибок за сегодня`,
        description: 'Откройте список последних ошибок и типы запросов, на которых они возникают.',
        tab: 'tech',
      });
    }
    if (downtime?.crash_count > 0) {
      items.push({
        key: 'downtime',
        type: 'warning',
        message: `Инцидентов за 30 дней: ${formatNumber(downtime.crash_count)}`,
        description: `Суммарный простой ${formatDuration(downtime.total_downtime_sec)}.`,
        tab: 'tech',
      });
    }
    if (overview?.blocked_count > 0) {
      items.push({
        key: 'blocked',
        type: 'info',
        message: `Заблокировано пользователей: ${formatNumber(overview.blocked_count)}`,
        description: 'Проверьте, все ли блокировки актуальны.',
        tab: 'users',
      });
    }
    return items;
  }, [summary.pendingAged, overview, downtime]);

  const timelineItems = useMemo(
    () =>
      mergeActivity(activity).map((item) => ({
        color: item.color,
        children: (
          <div className="admin-activity__item">
            <div className="admin-activity__head">
              <span className="admin-activity__title">{item.title}</span>
              <Tooltip title={formatDateTime(item.ts)}>
                <span className="admin-activity__time">{formatRelativeTime(item.ts)}</span>
              </Tooltip>
            </div>
            <div className="admin-activity__meta">
              {item.userId ? (
                <button
                  type="button"
                  className="admin-user-link"
                  onClick={() => setProfileUserId(item.userId)}
                >
                  {item.who}
                </button>
              ) : (
                <span>{item.who}</span>
              )}
              {item.meta ? <span> · {item.meta}</span> : null}
            </div>
          </div>
        ),
      })),
    [activity]
  );

  const topUserColumns = [
    {
      title: 'Пользователь',
      dataIndex: 'username',
      render: (username, row) => (
        <button type="button" className="admin-user-link" onClick={() => setProfileUserId(row.id)}>
          {username}
        </button>
      ),
    },
    {
      title: 'Запросов',
      dataIndex: 'requests_count',
      width: 110,
      align: 'right',
      render: (v) => formatNumber(v),
    },
    {
      title: 'Задач',
      dataIndex: 'tasks_count',
      width: 90,
      align: 'right',
      render: (v) => formatNumber(v),
    },
  ];

  const csvStats = overview?.csv_stats;

  return (
    <div className="admin-overview">
      <div className="admin-overview__toolbar">
        <Space size={8} wrap>
          <Text type="secondary">
            {summary.loading
              ? 'Обновляем данные…'
              : summary.updatedAt
                ? `Обновлено: ${formatShortDateTime(summary.updatedAt)}`
                : 'Данные ещё не загружены'}
          </Text>
          <Tag color="blue" bordered={false}>
            автообновление раз в минуту
          </Tag>
        </Space>
        <Button
          icon={<RefreshCw size={16} />}
          onClick={() => summary.refresh()}
          loading={summary.loading}
        >
          Обновить
        </Button>
      </div>

      {summary.error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Не удалось обновить сводку"
          description={summary.error}
          action={
            <Button size="small" onClick={() => summary.refresh()}>
              Повторить
            </Button>
          }
        />
      )}

      <Spin spinning={summary.loading || detailsLoading}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminOnlinePopover onlineUserIds={onlineIds} users={onlineUsers}>
              <div className="admin-kpi-card--clickable">
                <AdminKpiCard
                  title="Онлайн"
                  value={onlineCount}
                  icon={Users}
                  variant="green"
                  hint="Пользователи, у которых сейчас открыта сессия в сервисе"
                />
              </div>
            </AdminOnlinePopover>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Запросы сегодня"
              value={overview?.requests_today ?? 0}
              icon={Zap}
              variant="blue"
              hint="Поисковые подзапросы, созданные с начала суток"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Ошибки сегодня"
              value={overview?.error_period ?? 0}
              icon={AlertTriangle}
              variant="red"
              hint="Запросы со статусом ошибки с начала суток"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="В очереди > 30 мин"
              value={summary.pendingAged}
              icon={Hourglass}
              variant="orange"
              hint="Запросы в статусе «В очереди» старше 30 минут за последние 30 дней"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Активны за 7 дней"
              value={overview?.logged_in_7d ?? 0}
              icon={UserCheck}
              variant="cyan"
              hint="Пользователи, заходившие в сервис за последние 7 дней"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Заблокировано"
              value={overview?.blocked_count ?? 0}
              icon={UserX}
              variant="purple"
              hint="Пользователи с отключённым доступом"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Требует внимания" className="admin-chart-card">
              {attention.length ? (
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  {attention.map((item) => (
                    <Alert
                      key={item.key}
                      type={item.type}
                      showIcon
                      message={item.message}
                      description={item.description}
                      action={
                        onNavigate ? (
                          <Button size="small" onClick={() => onNavigate(item.tab)}>
                            Открыть
                          </Button>
                        ) : null
                      }
                    />
                  ))}
                </Space>
              ) : (
                <Alert
                  type="success"
                  showIcon
                  message="Критичных проблем не обнаружено"
                  description="Ошибок за сегодня нет, очередь не зависла, сервис работал без сбоев."
                />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Состояние сервиса" className="admin-chart-card">
              <Row gutter={[12, 16]}>
                <Col xs={12} sm={8}>
                  <Tooltip title="Доля запросов с ошибкой от всех запросов за сегодня">
                    <Statistic
                      title="Доля ошибок"
                      value={errorRate == null ? '—' : errorRate.toFixed(1)}
                      suffix={errorRate == null ? null : '%'}
                    />
                  </Tooltip>
                </Col>
                <Col xs={12} sm={8}>
                  <Tooltip title="Средняя длительность сессии пользователя за сегодня">
                    <Statistic
                      title="Средняя сессия"
                      value={formatDuration(overview?.avg_session_sec)}
                    />
                  </Tooltip>
                </Col>
                <Col xs={12} sm={8}>
                  <Tooltip title="Суммарное время недоступности за 30 дней">
                    <Statistic
                      title="Простой"
                      value={formatDuration(downtime?.total_downtime_sec)}
                    />
                  </Tooltip>
                </Col>
                <Col xs={12} sm={8}>
                  <Tooltip title="Среднее время восстановления после сбоя (MTTR)">
                    <Statistic title="MTTR" value={formatDuration(downtime?.mttr_sec)} />
                  </Tooltip>
                </Col>
                <Col xs={12} sm={8}>
                  <Tooltip title="Количество падений сервиса за 30 дней">
                    <Statistic title="Инциденты" value={downtime?.crash_count ?? 0} />
                  </Tooltip>
                </Col>
                <Col xs={12} sm={8}>
                  <Tooltip title="Активные записи в базе товарных знаков">
                    <Statistic
                      title="База ТЗ"
                      value={csvStats ? formatNumber(csvStats.active_records) : '—'}
                      suffix={csvStats ? `/ ${formatNumber(csvStats.total_records)}` : null}
                    />
                  </Tooltip>
                </Col>
              </Row>
              <Space wrap style={{ marginTop: 16 }}>
                <Button size="small" onClick={() => onNavigate?.('tech')}>
                  Тех. мониторинг
                </Button>
                <Button size="small" onClick={() => onNavigate?.('requests')}>
                  Запросы
                </Button>
                <Button size="small" onClick={() => onNavigate?.('audit')}>
                  Аудит
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Топ пользователей за 30 дней" className="admin-chart-card admin-table-card">
              <Table
                size="small"
                rowKey="id"
                dataSource={topUsers}
                columns={topUserColumns}
                pagination={false}
                loading={detailsLoading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет запросов за период" /> }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Последние события" className="admin-chart-card">
              {timelineItems.length ? (
                <Timeline items={timelineItems} className="admin-activity" />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Событий пока нет" />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>

      <UserDetailsDrawer
        open={profileUserId != null}
        userId={profileUserId}
        onClose={() => setProfileUserId(null)}
        getUser={getUser}
        isOnline={profileUserId != null && onlineSet.has(Number(profileUserId))}
      />
    </div>
  );
};

export default AdminOverview;
